import type { FC } from "react";

interface SessionToolbarProps {
	patientCode: string;
	formattedDate: string;
	eye: "left" | "right";
	onEyeChange: (eye: "left" | "right") => void;
	gridMode: "off" | "overlay" | "amsler";
	onGridModeChange: (mode: "off" | "overlay" | "amsler") => void;
	gridLineWidth: number;
	onGridLineWidthChange: (width: number) => void;
	imageZoom: number;
	onZoomIn: () => void;
	onZoomOut: () => void;
	onResetZoom: () => void;
	onSave: () => void;
	isSaving: boolean;
	onExportCSV: () => void;
	onExportImage: () => void;
	onBack: () => void;
	onReset: () => void;
	onUndo: () => void;
	canUndo: boolean;
	isViewMode?: boolean;
	viewDate?: string | null;
	onToggleViewMode?: () => void;
}

// Button style tiers (brand hierarchy)
const btn = {
	primary:
		"bg-accent-indigo text-white hover:bg-accent-indigo/90 focus:ring-accent-indigo/40",
	secondary:
		"bg-card border border-border text-foreground hover:bg-muted focus:ring-accent-indigo/30",
	ghost:
		"bg-transparent text-body-2 hover:bg-muted focus:ring-accent-indigo/30",
	destructive:
		"bg-transparent border border-destructive/30 text-destructive hover:bg-destructive/10 focus:ring-destructive/30",
	base: "px-2 lg:px-4 py-1 lg:py-2 rounded-lg font-semibold transition-colors shadow-sm focus:outline-none focus:ring-2 text-xs lg:text-sm whitespace-nowrap",
};

export const SessionToolbar: FC<SessionToolbarProps> = ({
	patientCode,
	formattedDate,
	eye,
	onEyeChange,
	gridMode,
	onGridModeChange,
	gridLineWidth,
	onGridLineWidthChange,
	imageZoom,
	onZoomIn,
	onZoomOut,
	onResetZoom,
	onSave,
	isSaving,
	onExportCSV,
	onExportImage,
	onBack,
	onReset,
	onUndo,
	canUndo,
	isViewMode,
	viewDate,
	onToggleViewMode,
}) => {
	return (
		<nav className="w-full z-30 border-b border-border bg-card shadow-sm mb-2 min-h-14">
			{/* Horizontal scrollable container */}
			<div
				className="flex items-center px-3 lg:px-4 py-3 overflow-x-auto overflow-y-hidden"
				style={{
					WebkitOverflowScrolling: "touch",
					overscrollBehavior: "contain",
					touchAction: "pan-x",
				}}
			>
				{/* Content container with minimum width to prevent squishing */}
				<div
					className="flex items-center gap-2 lg:gap-3 flex-nowrap"
					style={{ minWidth: "max-content" }}
				>
					{/* Left section: Back Button, Patient ID */}
					<div className="flex items-center gap-2 lg:gap-3 flex-shrink-0">
						<div className="flex flex-row items-center gap-2 lg:gap-3 flex-shrink-0">
							<button
								onClick={onBack}
								className={`${btn.base} ${btn.secondary} px-2 lg:px-3`}
								aria-label="Back to patients"
								type="button"
							>
								←
							</button>
							<div className="flex flex-col text-xs lg:text-sm font-medium min-w-0">
								<span className="font-bold text-foreground truncate">
									Patient Code:{" "}
									{patientCode || (
										<span className="text-dim">No patient selected</span>
									)}
								</span>
								<span className="text-body-3 truncate">{formattedDate}</span>
							</div>
							{isViewMode && viewDate && (
								<>
									<span className="text-xs lg:text-sm font-medium text-dim whitespace-nowrap">
										Viewing: {new Date(viewDate).toLocaleString()}
									</span>
									{onToggleViewMode && (
										<button
											type="button"
											onClick={onToggleViewMode}
											className={`${btn.base} ${btn.primary} px-2`}
										>
											Edit
										</button>
									)}
								</>
							)}
							{!isViewMode && viewDate && onToggleViewMode && (
								<button
									type="button"
									onClick={onToggleViewMode}
									className={`${btn.base} ${btn.secondary} px-2`}
								>
									View Only
								</button>
							)}
						</div>

						{/* Eye Selection */}
						<div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
							<span className="text-xs lg:text-sm font-medium text-body-2 whitespace-nowrap">
								Eye:
							</span>
							<button
								type="button"
								className={`px-2 lg:px-3 py-1 lg:py-2 rounded-l-lg border font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-indigo/40 text-xs lg:text-sm whitespace-nowrap ${
									eye === "left"
										? "bg-accent-indigo text-white border-accent-indigo shadow-sm scale-105"
										: "bg-card text-accent-indigo border-border hover:bg-muted"
								}`}
								onClick={() => onEyeChange("left")}
							>
								Left
							</button>
							<button
								type="button"
								className={`px-2 lg:px-3 py-1 lg:py-2 rounded-r-lg border-l-0 border font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-indigo/40 text-xs lg:text-sm whitespace-nowrap ${
									eye === "right"
										? "bg-accent-indigo text-white border-accent-indigo shadow-sm scale-105"
										: "bg-card text-accent-indigo border-border hover:bg-muted"
								}`}
								onClick={() => onEyeChange("right")}
							>
								Right
							</button>
						</div>

						{/* Grid Mode + Width */}
						<div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
							<span className="text-xs lg:text-sm font-medium text-body-2 whitespace-nowrap">
								Grid:
							</span>
							{(["off", "overlay", "amsler"] as const).map((mode, idx, arr) => {
								const isFirst = idx === 0;
								const isLast = idx === arr.length - 1;
								const radius = isFirst
									? "rounded-l-lg"
									: isLast
										? "rounded-r-lg border-l-0"
										: "border-l-0";
								const selected = gridMode === mode;
								return (
									<button
										key={mode}
										type="button"
										className={`px-2 lg:px-3 py-1 lg:py-2 ${radius} border font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-accent-indigo/40 text-xs lg:text-sm whitespace-nowrap capitalize ${
											selected
												? "bg-accent-indigo text-white border-accent-indigo shadow-sm scale-105"
												: "bg-card text-accent-indigo border-border hover:bg-muted"
										}`}
										onClick={() => onGridModeChange(mode)}
									>
										{mode}
									</button>
								);
							})}
							{gridMode !== "off" && (
								<>
									<input
										type="range"
										min={1}
										max={10}
										step={1}
										value={gridLineWidth}
										onChange={(e) =>
											onGridLineWidthChange(Number(e.target.value))
										}
										className="w-16 lg:w-20 accent-accent-indigo"
										aria-label="Grid line width"
									/>
									<span className="text-xs text-body-3 tabular-nums whitespace-nowrap">
										{gridLineWidth}px
									</span>
								</>
							)}
						</div>
					</div>

					{/* Spacer */}
					<div className="flex-grow min-w-[2rem]" />

					{/* Right section: Zoom, Export buttons */}
					<div className="flex items-center gap-2 flex-shrink-0">
						{/* Zoom Controls */}
						<div className="flex items-center gap-1 flex-shrink-0">
							<span className="text-xs font-medium text-body-2 whitespace-nowrap">
								Zoom:
							</span>
							<button
								type="button"
								onClick={onZoomOut}
								disabled={imageZoom <= 0.5}
								className="w-7 h-7 text-[1rem] font-bold bg-muted text-foreground rounded-lg hover:bg-border active:bg-octonary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Zoom out"
							>
								−
							</button>
							<span className="text-xs font-semibold min-w-[2rem] text-center text-foreground tabular-nums">
								{imageZoom.toFixed(1)}x
							</span>
							<button
								type="button"
								onClick={onZoomIn}
								disabled={imageZoom >= 2.0}
								className="w-7 h-7 text-[1rem] font-bold bg-muted text-foreground rounded-lg hover:bg-border active:bg-octonary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								aria-label="Zoom in"
							>
								+
							</button>
							<button
								type="button"
								onClick={onResetZoom}
								className={`${btn.base} ${btn.ghost} px-2`}
							>
								Reset
							</button>
						</div>

						{/* Save & Export Buttons */}
						{!isViewMode && (
							<button
								type="button"
								onClick={onSave}
								disabled={isSaving}
								className={`${btn.base} ${btn.primary} disabled:opacity-50`}
							>
								{isSaving ? "Saving..." : "Save"}
							</button>
						)}
						<button
							type="button"
							onClick={onExportCSV}
							className={`${btn.base} ${btn.secondary}`}
						>
							Export CSV
						</button>
						<button
							type="button"
							onClick={onExportImage}
							className={`${btn.base} ${btn.secondary}`}
						>
							Export Image
						</button>
						{!isViewMode && (
							<button
								type="button"
								onClick={onUndo}
								disabled={!canUndo}
								className={`${btn.base} ${btn.secondary} ${
									canUndo ? "" : "opacity-50 cursor-not-allowed"
								}`}
							>
								Undo
							</button>
						)}
						{!isViewMode && (
							<button
								type="button"
								onClick={onReset}
								className={`${btn.base} ${btn.destructive}`}
							>
								Reset
							</button>
						)}
					</div>
				</div>
			</div>
		</nav>
	);
};
